import { ChangeEvent, useCallback, useEffect, useState } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { Layout } from "../../components/Layout";
import { UserCreateParams, UserLoginParams } from "../../../../types/user";
import { usePushMessage } from "../../providers";
import { useAuthTools } from "../../providers/authProvider";
import { Button } from "../../components/Button";
import {
  ART_LIST_URL,
  AuthParams,
  CHECKOUT_URL,
  PARAM_KEYS,
  USER_AUTH_URL,
  UserAuthPaths,
} from "../../config";
import { Input } from "../../components/forms";
type UserFormValues = UserCreateParams & UserLoginParams;

type FormType = {
  email: string;
  password: string;
  "confirm-password": string;
  name: string;
  code: string;
};

type InputType<Form> = {
  id: string;
  name: keyof Form;
  type?: "password" | "email" | "text"; // defaults to text
  label: string;
  placeholder: string;
  autoComplete: string;
  required?: true;
  disabled?: boolean;
};
const Timer = ({
  expire,
  onTimeout,
}: {
  expire: number;
  onTimeout: () => void;
}) => {
  const [timeLeft, setTimeLeft] = useState({ string: "", warning: false });
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (expire) {
      if (expire - new Date().getTime() / 1000 > 0) {
        setTimedOut(false);
      }
      if (!timedOut) {
        const id = setInterval(() => {
          const secsLeft = expire - new Date().getTime() / 1000;
          if (secsLeft < 1) {
            setTimeLeft({
              string:
                "Times Up (code expired at" +
                new Intl.DateTimeFormat("en-us", {
                  hour12: true,
                  hour: "numeric",
                  minute: "2-digit",
                  second: "2-digit",
                  // timeZoneName: "shortGeneric",
                }).format(expire * 1000) +
                ")",
              warning: true,
            });
            onTimeout();
            setTimedOut(false);
          } else {
            const sec = Math.floor(secsLeft % 60);
            const min = Math.floor(secsLeft / 60);
            const hour = Math.floor(secsLeft / 60 / 60);

            const timeString =
              (hour
                ? `${hour}:${String(min).padStart(2, "0")} hours`
                : min
                  ? `${min}:${String(sec).padStart(2, "0")} mins`
                  : `${sec} seconds!`) +
              " (at " +
              new Intl.DateTimeFormat("en-us", {
                hour12: true,
                hour: "numeric",
                minute: "2-digit",
                second: "2-digit",
                // timeZoneName: "shortGeneric",
              }).format(expire * 1000) +
              ")";
            setTimeLeft({ string: timeString, warning: !hour && !min });
          }
        }, 1000);
        return () => clearInterval(id);
      }
    }
  }, [expire, timedOut, setTimedOut, setTimeLeft]);
  if (!expire || !timeLeft) {
    return null;
  }
  return (
    <span className={timeLeft.warning ? "text-textWarning" : ""}>
      {timeLeft.string}
    </span>
  );
};
// const Input = ({
//   label,
//   error,
//   ...props
// }: JSX.IntrinsicElements["input"] & {
//   label: string;
//   error?: string;
// }) => {
//   const id = props.id ?? props.name;
//   return (
//     <div className="my-3 flex flex-col items-start ">
//       <label className="mb-1 text-textMain" htmlFor={id}>
//         {label}
//       </label>
//       <input
//         className=" p-4 rounded w-[250px] md:w-[500px]"
//         {...props}
//         id={id}
//       />
//       <div> {error}</div>
//     </div>
//   );
// };

type GetTextReturnType = {
  title: string;
  inputs: InputType<FormType>[];
  CTA: { text: string };
  secondaryCTA?: { text: string; href: string; reset?: boolean };
  tertiaryCTA?: { text: string; href: string; reset?: boolean };
};
const getText = ({
  params: { to, flow },
  pageSlug,
  challengeCreated,
  challengeVerified,
  passwordReset,
}: {
  pageSlug: UserAuthPaths;
  params: AuthParams;
  challengeCreated?: boolean;
  challengeVerified?: boolean;
  passwordReset?: boolean;
}): GetTextReturnType => {
  const name = {
    id: "name",
    label: "Name",
    name: "name",
    placeholder: "Alex Decanter",
    autoComplete: "name",
    required: true,
  } as const;
  const email = {
    id: "email",
    label: "Email",
    name: "email",
    placeholder: "SparkySilver@gmail.com",
    autoComplete: "email",
    type: "email",
    required: true,
  } as const;
  const password = {
    id: "password",
    label: "Password",
    name: "password",
    type: "password",
    placeholder: "S upe80!!r Sec!r  et!P!a s9!sw1od1212!r41d",
    autoComplete: "current-password",
    required: true,
  } as const;

  const code = {
    id: "challengeCode",
    label: "Code",
    name: "code",
    type: "text",
    placeholder: "Look in your email for the code",
    autoComplete: "none",
    required: true,
  } as const;
  const CTA = { text: "Submit" };

  if (pageSlug === "login") {
    return {
      title: "Login here",
      inputs: [email, password],
      CTA,
      secondaryCTA: {
        text: "New here?",
        href: USER_AUTH_URL("create", {
          flow,
          to,
        }),
      },
      tertiaryCTA: {
        text: "Forgot your password?",
        href: USER_AUTH_URL("challenge-password-reset", {
          flow,
          to,
        }),
        reset: true,
      },
    };
  } else if (pageSlug === "create") {
    return {
      title: "Signup!",
      inputs: [
        name,
        email,
        {
          ...password,
          autoComplete: "new-password",
          required: true,
        },
        {
          ...password,
          autoComplete: "new-password",
          name: "confirm-password",
          label: "Confirm password",
          id: "new-password",
          required: true,
        },
      ],
      CTA,
      secondaryCTA: {
        text: "Been here before?",
        href: USER_AUTH_URL("login", {
          flow,
          to,
        }),
      },
    };
  } else if (pageSlug === "challenge-login") {
    return challengeCreated
      ? {
          title: "Enter the code from your email",
          inputs: [code],
          CTA: {
            text: "Log in this time",
          },
          secondaryCTA: {
            text: "Want to change your password?",
            href: USER_AUTH_URL("challenge-password-reset", {
              flow,
              to,
            }),
          },
          tertiaryCTA: {
            text: "Didn't get an email? Create an account",
            href: USER_AUTH_URL("create", {
              flow,
              to,
            }),
          },
        }
      : {
          title: "Need to log in with you email?",
          inputs: [email],
          CTA: {
            text: "Send email with code",
          },
          secondaryCTA: {
            text: "Reset your password",
            href: USER_AUTH_URL("challenge-password-reset", {
              flow,
              to,
            }),
          },
          tertiaryCTA: {
            text: "Login with password",
            href: USER_AUTH_URL("login", {
              flow,
              to,
            }),
          },
        };
  } else if (pageSlug === "challenge-password-reset") {
    return passwordReset
      ? {
          title: "Password has been reset!",
          inputs: [],
          CTA: {
            text: "Try your new password out!",
          },
        }
      : challengeVerified
        ? {
            title: "Enter your new password",
            inputs: [
              {
                ...password,
                autoComplete: "new-password",
                required: true,
              },
              {
                ...password,
                autoComplete: "new-password",
                name: "confirm-password",
                label: "Confirm password",
                id: "new-password",
                required: true,
              },
            ],
            CTA: {
              text: "Update password",
            },
            secondaryCTA: {
              text: "Back to login",
              href: USER_AUTH_URL("login", {
                flow,
                to,
              }),
            },
            tertiaryCTA: {
              text: "Create an account",
              href: USER_AUTH_URL("create", {
                flow,
                to,
              }),
            },
          }
        : challengeCreated
          ? {
              title: "Enter the code from your email",
              inputs: [code],
              CTA: {
                text: "Change password",
              },
              secondaryCTA: {
                text: "Want to just login?",
                href: USER_AUTH_URL("challenge-login", {
                  flow,
                  to,
                }),
              },
              tertiaryCTA: {
                text: "Didn't get an email? Create an account",
                href: USER_AUTH_URL("create", {
                  flow,
                  to,
                }),
              },
            }
          : {
              title: "Forgot your password?",
              inputs: [email],
              CTA: {
                text: "Send email with code",
              },
              tertiaryCTA: {
                text: "Remember your password?",
                href: USER_AUTH_URL("login", {
                  flow,
                  to,
                }),
              },
            };
  } else {
    throw Error("NOT SET UP TO HANDLE PAGE :'" + pageSlug + "'");
  }
};
const baseForm = {
  password: "",
  email: "",
  "confirm-password": "",
  name: "",
  code: "",
};
export const Auth = ({ pageSlug }: { pageSlug: UserAuthPaths }) => {
  const {
    login,
    signup,
    loginWithChallenge,
    createChallenge,
    changePassword,
    validateChangePassword,
  } = useAuthTools();
  const [challengeCreated, setChallengeCreated] = useState(false);
  const [challengeVerified, setChallengeVerified] = useState(false);
  const [passwordReset, setPasswordReset] = useState(false);
  const [expireAt, setExpireAt] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const queryFlow = searchParams.get(
    PARAM_KEYS.AUTH_REDIRECT_FLOW.QUEARY_PARAM,
  );
  const queryTo = searchParams.get(PARAM_KEYS.AUTH_REDIRECT_TO.QUEARY_PARAM);

  const flow = queryFlow as AuthParams["flow"];
  const to = queryTo as AuthParams["to"];

  const [validationError, setValidationError] = useState("");

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormType>(baseForm);
  const reset = useCallback(() => {
    setForm(() => baseForm);
    setChallengeCreated(false);
    setChallengeVerified(false);
    setExpireAt(0);
    setClicked(0);
  }, [setForm, setChallengeCreated, setChallengeVerified, setExpireAt]);
  const pushMessage = usePushMessage();
  const validate = (form: FormType, inputs: InputType<FormType>[]) => {
    const firstMissingRequiredInput = inputs
      .filter((input) => !!input.required)
      .find((input) => !form[input.name]);
    if (firstMissingRequiredInput) {
      return "Please enter " + firstMissingRequiredInput.label;
    }

    if (pageSlug === "login") {
      return true;
    } else if (pageSlug === "create") {
      if (form["confirm-password"] !== form.password) {
        return "Passwords do not match, please verify";
      }
      return true;
    } else if (pageSlug === "challenge-login") {
      return true;
    } else if (pageSlug === "challenge-password-reset") {
      if (form.password && form["confirm-password"] !== form.password) {
        return "Please make sure the password and confirm password match";
      }
      return true;
    } else {
      throw Error('unknown page slug "' + pageSlug + '"');
    }
  };

  const { title, inputs, CTA, secondaryCTA, tertiaryCTA } = getText({
    params: {
      flow,
      to,
    },
    pageSlug,
    challengeCreated,
    challengeVerified,
    passwordReset,
  });

  const gainAccess = useCallback(
    ({
      email,
      password,
      name,
      code,
    }: FormType): Promise<void | undefined> | undefined => {
      if (!login) {
        console.log("LOGIN/SIGNUP DOESN'T EXIST YET!");
        return;
      }

      if (passwordReset) {
        setPasswordReset(false);
        navigate(USER_AUTH_URL("login"));
        return;
      }

      //TODO Validation
      setLoading(true);
      const validationResult = validate(form, inputs);
      if (typeof validationResult === "string") {
        setLoading(false);
        setValidationError(validationResult);
        return;
      } else if (!validationResult) {
        setLoading(false);
        setValidationError("Please check the fields are valid");
        return;
      }
      setValidationError("");
      return (
        pageSlug === "login"
          ? login({ username: email, password })
          : pageSlug === "create"
            ? signup({ email, username: email, password, name })
            : pageSlug === "challenge-login"
              ? challengeCreated
                ? loginWithChallenge({ email, code })
                : createChallenge({ email })
              : pageSlug === "challenge-password-reset"
                ? challengeVerified
                  ? changePassword({ email, code, password })
                  : challengeCreated
                    ? validateChangePassword({ email, code })
                    : createChallenge({ email })
                : Promise.resolve({
                    success: false,
                    error: "no action defined for slug",
                  })
      )
        .then(
          (data: {
            success: boolean;
            user?: any;
            error?: string;
            expiration?: number;
          }) => {
            if (data.success) {
              setExpireAt(0);

              if (
                (pageSlug === "challenge-login" ||
                  pageSlug === "challenge-password-reset") &&
                !code
              ) {
                setChallengeCreated(true);
                pushMessage({
                  message:
                    "We'll send a code to " +
                    email +
                    " if we find it in our db!",
                  type: "info",
                  ttl: 10000,
                });
                setLoading(false);

                return;
              }
              if (pageSlug === "challenge-password-reset" && !password) {
                setChallengeVerified(true);
                if (data.expiration) {
                  pushMessage({
                    message:
                      "We've confirmed that code, it will expire at " +
                      new Intl.DateTimeFormat("en-us", {
                        hour12: true,
                        hour: "numeric",
                        minute: "2-digit",
                        second: "2-digit",
                        timeZoneName: "shortGeneric",
                      }).format(new Date()),
                    type: "info",
                    ttl: 10000,
                  });
                  setExpireAt(
                    Math.floor(new Date(data.expiration).getTime() / 1000),
                  );
                }
                setLoading(false);

                return;
              }
              if (pageSlug === "challenge-password-reset" && password) {
                setPasswordReset(true);
                // pushMessage({
                //   message:
                //     "Your password has been reset, go ahead and try to log in to see if everything works as expected",
                //   type: "info",
                //   ttl: 10000,
                // });
                setLoading(false);
                reset();
                return;
              }

              const redirect =
                flow === "checkout"
                  ? CHECKOUT_URL()
                  : to
                    ? to
                    : ART_LIST_URL("basic", data?.user?.admin);
              navigate(redirect);
            } else {
              console.log(data);
              setValidationError(data.error as string);
              setLoading(false);
            }
            reset();
            // setLoading(false);
          },
        )
        .catch((e) => {
          console.log(e);
          setLoading(false);
          pushMessage({
            message: e.message,
            type: "error",
            ttl: 10000,
          });
        });
    },
    [
      pageSlug,
      setLoading,
      navigate,
      login,
      signup,
      inputs,
      challengeCreated,
      challengeVerified,
      passwordReset,
      form,
      flow,
      to,
    ],
  );

  const [clicked, setClicked] = useState(0);
  useEffect(() => {
    setClicked(0);
  }, [setClicked, location.pathname]);
  if (!pageSlug) {
    return null;
  }
  const handleChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = evt.target;
    setForm((form) => ({
      ...form,
      [name]: value,
    }));
  };
  const buttonClass = " duration-700  " + (clicked ? " opacity-0 " : "");

  return (
    <Layout>
      <div>
        <form
          className="flex flex-col items-center px-2"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setClicked(1);
            try {
              gainAccess(form)?.then(() => {
                setClicked(0);
              });
            } catch (e: any) {
              pushMessage({ type: "error", message: e.message });
            }
          }}
        >
          <h1>{title}</h1>

          {expireAt && pageSlug === "challenge-password-reset" ? (
            <div>
              Your reset session will expire in{" "}
              <Timer
                expire={expireAt}
                onTimeout={() => {
                  setExpireAt(0);
                  reset();
                  setLoading(false);

                  pushMessage({
                    message:
                      "Your password reset session has expired, please try again",
                    type: "info",
                  });
                }}
              />
            </div>
          ) : null}
          {inputs.map((inputDetails) => (
            <Input
              inputType="input"
              key={inputDetails.id}
              className={"max-w-full w-[500px] my-2 "}
              {...inputDetails}
              disabled={inputDetails.disabled || loading}
              value={form[inputDetails.name]}
              onChange={handleChange}
            />
          ))}
          <input
            // inputType="input"
            className={"opacity-0 max-h-0 touch-none"}
            name={"supportLevel"}
            // label="Support level"
            disabled={loading}
            value={(form as any)["supportLevel"]}
            onChange={handleChange}
          />
          <div className="text-textWarning">{validationError}</div>
          <div className=" my-2 w-full  max-w-[500px]">
            <Button
              type="submit"
              loading={loading}
              disabled={loading}
              className={clicked !== 1 ? buttonClass : undefined}
              btnColor="primary"
              btnType="large"
            >
              {CTA.text}
            </Button>
          </div>
          {secondaryCTA ? (
            <div className="my-2 w-full max-w-[500px]">
              <Button
                type="button"
                loading={loading}
                disabled={loading}
                btnColor="secondary"
                btnType="large"
                className={clicked !== 2 ? buttonClass : undefined}
                onClick={() => {
                  setClicked(2);
                  if (secondaryCTA.reset) {
                    reset();
                  }
                  secondaryCTA.href === location.pathname
                    ? window.location.reload()
                    : navigate(secondaryCTA.href);
                  setForm((form) => ({
                    ...form,
                    password: "",
                    "confirm-password": "",
                  }));
                }}
              >
                {secondaryCTA.text}
              </Button>
            </div>
          ) : null}

          {tertiaryCTA ? (
            <div className="my-2 w-full max-w-[500px]">
              <Button
                type="button"
                loading={loading}
                disabled={loading}
                btnColor="text"
                className={clicked !== 3 ? buttonClass : undefined}
                btnType="text"
                onClick={() => {
                  setClicked(3);
                  if (tertiaryCTA.reset) {
                    reset();
                  }
                  tertiaryCTA.href === location.pathname
                    ? window.location.reload()
                    : navigate(tertiaryCTA.href);
                  setForm((form) => ({
                    ...form,
                    password: "",
                    "confirm-password": "",
                  }));
                }}
              >
                {tertiaryCTA.text}
              </Button>
            </div>
          ) : null}
        </form>
      </div>
    </Layout>
  );
};

// Wrapper component that uses React Router
export function AuthPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Validate slug
  const validSlugs: UserAuthPaths[] = [
    "login",
    "create",
    "challenge-login",
    "challenge-password-reset",
  ];

  if (!slug || !validSlugs.includes(slug as UserAuthPaths)) {
    // Invalid slug, redirect to home
    navigate("/", { replace: true });
    return null;
  }

  return <Auth pageSlug={slug as UserAuthPaths} />;
}
export default AuthPage;
